import AddBadge from "atoms/badges/add-badge";
import Button from "atoms/buttons/button";
import { IconSizes } from "atoms/constants/icon-sizes";
import Icon from "atoms/icons/icon";
import {
    NotebookPageContentBlock,
    NotebookPageContentBlockCultureCode,
    NotebookPageContentBlockIcon,
} from "constants/notebooks/notebook-page-content-blocks";
import MenuButton from "molecules/menu-button/menu-button";
import React, { useState, useEffect } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { t } from "utilities/localization-utils";
import { useSetAtom } from "jotai";
import { isAddBlockMenuOpen } from "utilities/atoms/page-blocks-atom";
import { useRunOnUnmount } from "utilities/hooks/use-run-on-unmount";
import UsePageLimits from "pages/notebooks/use-page-limits";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";

interface PageBlockAddMenuProps {
    cssClassName?: string;
    onAddBlockBelow: (type: NotebookPageContentBlock) => void;
    size?: IconSizes;
    showMenuOnLeft?: boolean;
}

const CSS_CLASS_NAME = "c-page-block-add-menu";
const TOOLTIP_DELAY = 500;
const PageBlockAddMenu = (props: PageBlockAddMenuProps) => {
    const {
        onAddBlockBelow,
        cssClassName = "",
        size = IconSizes.Medium,
        showMenuOnLeft = false,
    } = props;
    const { pageBlockLimitReached: disabled, codeReferenceBlockLimitReached } =
        UsePageLimits();
    const setIsAddBlockMenuOpen = useSetAtom(isAddBlockMenuOpen);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const disableAddBlockType = (type: NotebookPageContentBlock) => {
        if (type === NotebookPageContentBlock.CodeReference) {
            return codeReferenceBlockLimitReached;
        }
        return false;
    };

    useEffect(() => {
        setIsAddBlockMenuOpen(isMenuOpen);
    }, [isMenuOpen, setIsAddBlockMenuOpen]);

    useRunOnUnmount(() => setIsAddBlockMenuOpen(false));

    return (
        <Tooltip
            content={t("exceedsBlockLimit")}
            cssClassName={`${CSS_CLASS_NAME}__tooltip`}
            delayInMs={TOOLTIP_DELAY}
            disabled={!disabled}
            placement={TooltipPlacement.Bottom}>
            <div
                className={`${CSS_CLASS_NAME} ${cssClassName} ${showMenuOnLeft ? "-left" : ""} ${disabled ? "-disabled" : ""}`}>
                <MenuButton
                    correctMenuPlacement={!showMenuOnLeft}
                    customButtonContent={<AddBadge size={size} />}
                    externalIsOpen={isMenuOpen}
                    externalSetIsOpen={setIsMenuOpen}
                    disabled={disabled}>
                    {CollectionUtils.mapEnum(NotebookPageContentBlock).map(
                        (type) => (
                            <Tooltip
                                content={t("exceedsCodeReferenceLimit")}
                                cssClassName={`${CSS_CLASS_NAME}__tooltip`}
                                placement={TooltipPlacement.Bottom}
                                disabled={
                                    type !==
                                        NotebookPageContentBlock.CodeReference ||
                                    (type ===
                                        NotebookPageContentBlock.CodeReference &&
                                        !codeReferenceBlockLimitReached)
                                }>
                                <Button
                                    key={type}
                                    cssClassName={`${CSS_CLASS_NAME}__button ${disableAddBlockType(type) ? "-disabled" : ""}`}
                                    onClick={() => {
                                        onAddBlockBelow(type);
                                    }}>
                                    <span
                                        className={`${CSS_CLASS_NAME}__buttonContent`}>
                                        <Icon
                                            cssClassName={`${CSS_CLASS_NAME}__icon`}
                                            type={
                                                NotebookPageContentBlockIcon[
                                                    type
                                                ]
                                            }
                                        />
                                        <span
                                            className={`${CSS_CLASS_NAME}__text`}>
                                            {t(
                                                NotebookPageContentBlockCultureCode[
                                                    type
                                                ]
                                            )}
                                        </span>
                                    </span>
                                </Button>
                            </Tooltip>
                        )
                    )}
                </MenuButton>
            </div>
        </Tooltip>
    );
};

export default PageBlockAddMenu;
